import { PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form, Spin } from "antd"
import { useWatch } from "antd/es/form/Form"
import dayjs from "dayjs"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { receiveErrors } from "modules/errors/reducer"
import { serialize } from "object-to-formdata"
import { useEffect, useState, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { getGuideTrip, updateTrip, updatedSeasonal } from "store/trips"
import { Card } from "ui/Card"
import { CustomForm } from "ui/Form"
import { DateRangeBlock } from "./DateRangeBlock"
import { DaysAndHours } from "./DaysAndHours"
import { LengthAndRates } from "./LengthAndRates"
import { SeasonTypeSelector } from "./SeasonTypeSelector"
import { GuestDetails } from "../ListingDetails/GuestDetails"
import { PRICING_TYPES, SEASON_TYPES } from "./utils/constants"
import { setFormTouched, setFormValues } from "store/proNewListingForms"

export const ListingScheduling = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const formScheduling = useSelector((state) => state.proNewListingForms.scheduling)
  const [form] = Form.useForm()
  const watchedStartDates = useWatch([], form)
  const trip = useSelector(modelSelector("trip"))
  const { licenses_required, licenses_list } = trip
  const [isSeasonalExperience, setIsSeasonalExperience] = useState(false)
  const [timeBlocks, setTimeBlocks] = useState([0])
  const [isLoading, setIsLoading] = useState(false)
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (id) {
      hasInitialized.current = false // Reset when navigating to a different trip
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    // Only initialize form values once when trip data first loads
    // This prevents resetting the form when trip is refetched later (e.g. via updatedAvailableHours)
    if (trip && Object.keys(trip).length && !hasInitialized.current) {
      // 1. Initialize Seasonal State & TimeBlocks
      // Check if seasonal is explicitly set (could be true or false)
      const isSeasonal = trip.seasonal === true
      setIsSeasonalExperience(isSeasonal)

      if (isSeasonal) {
        if (trip.available_date_ranges?.length > 0) {
          setTimeBlocks(Array.from({ length: trip.available_date_ranges.length }, (_, i) => i))
        } else {
          setTimeBlocks([0])
        }
      } else {
        setTimeBlocks([])
      }

      // 2. Initialize Form Fields
      form.setFieldsValue({
        pricing_type: trip.pricing_type || PRICING_TYPES.PER_PERSON,
        deposit_percentage: trip.deposit_percentage || 10,
        full_payment_required: trip.full_payment_required,
        min_guests: trip.min_guests,
        max_guests: trip.max_guests,
        licenses_required: trip.licenses_required,
        licenses_list: trip.licenses_list,
        recurringYearly: trip.available_date_ranges?.length > 0 ? trip.recurring_yearly : true
      })

      // 3. Sync initial trip data to Redux form state
      const initialReduxValues = {
        pricing_type: trip.pricing_type || PRICING_TYPES.PER_PERSON,
        deposit_percentage: trip.deposit_percentage || 10,
        full_payment_required: trip.full_payment_required,
        min_guests: trip.min_guests,
        max_guests: trip.max_guests,
        licenses_required: trip.licenses_required,
        licenses_list: trip.licenses_list,
        seasonal: trip.seasonal,
        available_days: trip.available_days,
        recurringYearly: trip.available_date_ranges?.length > 0 ? trip.recurring_yearly : true
      }

      if (trip.available_date_ranges) {
        trip.available_date_ranges.forEach((range, index) => {
          if (range.start) initialReduxValues[`seasonStart_${index}`] = dayjs(range.start)
          if (range.end) initialReduxValues[`seasonEnd_${index}`] = dayjs(range.end)
        })
      }

      dispatch(setFormValues("scheduling", initialReduxValues))
      hasInitialized.current = true
    }
  }, [trip, form, dispatch])

  const handleSeasonChange = (e) => {
    const isSeasonal = e.target.value === SEASON_TYPES.SEASONAL
    setIsSeasonalExperience(isSeasonal)

    if (isSeasonal && timeBlocks.length === 0) {
      setTimeBlocks([0])
    }



    // Update Redux form state for validation (merge with existing values)
    dispatch(setFormValues("scheduling", {
      ...formScheduling?.values,
      seasonal: isSeasonal
    }))

    // Also update main trip store so it doesn't revert when other fields update
    dispatch(updatedSeasonal(isSeasonal))

    dispatch(setFormTouched("scheduling", true))
  }

  const handleAddTimeBlock = () => {
    setTimeBlocks((prevBlocks) => {
      const nextIndex = prevBlocks.length ? Math.max(...prevBlocks) + 1 : 0
      return [...prevBlocks, nextIndex]
    })
  }

  const handleRemoveTimeBlock = (index) => {
    setTimeBlocks((prevBlocks) => prevBlocks.filter((blockIndex) => blockIndex !== index))

    // Clean up Redux form state to prevents validation errors from deleted blocks
    const currentValues = { ...formScheduling?.values }
    delete currentValues[`seasonStart_${index}`]
    delete currentValues[`seasonEnd_${index}`]
    dispatch(setFormValues("scheduling", currentValues))
    dispatch(setFormTouched("scheduling", true))
  }

  const prepareUpdateData = async (validateForm = false) => {
    // to flat rate, allowing the form to submit without new flat rate entries.
    const selectedPricingType = form.getFieldValue("pricing_type") || trip.pricing_type || PRICING_TYPES.PER_PERSON
    const tripPrices = (trip.trip_prices || []).filter((p) => !p.pricing_type || p.pricing_type === selectedPricingType)
    if (tripPrices.length === 0) {
      dispatch(
        receiveErrors({
          data: { message: { listing_rates: [t("experiences.scheduling.sections.length_and_rates.no_rates_error_message")] } },
          showToast: false
        })
      )
      throw t("experiences.scheduling.sections.length_and_rates.no_rates_error_message")
    }
    const values = validateForm ? await form.validateFields() : form.getFieldsValue(true)

    const updateData = {
      time_zone: values.time_zone,
      // Normalize available_days to ensure selected days send true
      available_days:
        values.available_days && typeof values.available_days === "object"
          ? Object.keys(values.available_days).reduce((acc, day) => {
            acc[day] = !!values.available_days[day]
            return acc
          }, {})
          : values.available_days,
      available_hours: values.available_hours
    }

    updateData.seasonal = isSeasonalExperience

    if (isSeasonalExperience) {
      updateData.recurring_yearly = values.recurringYearly

      const formatDateRanges = (values, timeBlocks, keyMap) =>
        timeBlocks
          .map((blockIndex) => {
            const startDate = values[`seasonStart_${blockIndex}`]
            const endDate = values[`seasonEnd_${blockIndex}`]

            if (startDate && endDate) {
              return {
                [keyMap.start]: startDate.format("YYYY-MM-DD"),
                [keyMap.end]: endDate.format("YYYY-MM-DD")
              }
            }
            return null
          })
          .filter(Boolean)

      const dateRanges = formatDateRanges(values, timeBlocks, {
        start: "start",
        end: "end"
      })

      const seasonalTripsAttributes = formatDateRanges(values, timeBlocks, {
        start: "start_date",
        end: "end_date"
      })

      if (dateRanges.length > 0) {
        updateData.available_date_ranges = dateRanges
        updateData.seasonal_trips_attributes = seasonalTripsAttributes
      }
    }

    updateData.pricing_type = values.pricing_type
    updateData.full_payment_required = values.full_payment_required
    updateData.deposit_percentage = values.deposit_percentage


    updateData.available_hours = values?.available_hours
      ? values?.available_hours.map(({ start, end }) => ({
        start: start.format("HH:mm"),
        end: end.format("HH:mm")
      }))
      : []
    updateData.trip_slots_attributes = values?.available_hours
      ? values?.available_hours.map(({ start, end }) => ({
        start_time: start.format("HH:mm"),
        end_time: end.format("HH:mm")
      }))
      : []
    return { ...values, ...updateData }
  }

  const onFinish = async (callback) => {
    setIsShowErrorMessage(true)
    try {
      setIsLoading(true)
      const updateData = await prepareUpdateData(true)
      const formData = serialize({ trip: updateData }, { allowEmptyArrays: true })
      await dispatch(updateTrip(id, formData))
        .then(() => {
          dispatch(setFormTouched("scheduling", false))
          callback?.()
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    } catch (error) {
      setIsLoading(false)
      console.error("Form validation failed:", error)
    }
  }

  const getAllSelectedDateRanges = useMemo(() => {
    return timeBlocks
      .map((blockIndex) => {
        const start = form.getFieldValue(`seasonStart_${blockIndex}`)
        const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
        if (start && end) {
          return { blockIndex, start, end }
        }
        return null
      })
      .filter(Boolean)
  }, [form, timeBlocks, watchedStartDates])

  // Track form changes in Redux for tab switch validation


  const handleValuesChange = (_, allValues) => {
    const currentValues = {
      ...formScheduling?.values,
      ...allValues
    }

    dispatch(setFormValues("scheduling", currentValues))
    if (!formScheduling?.touched) {
      dispatch(setFormTouched("scheduling", true))
    }
  }

  const handlePriceInteraction = () => {
    // Sync current form values to Redux (especially pricing_type)
    const currentFormValues = form.getFieldsValue(true)
    const currentValues = {
      ...formScheduling?.values,
      ...currentFormValues
    }
    dispatch(setFormValues("scheduling", currentValues))
    dispatch(setFormTouched("scheduling", true))
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm
        form={form}
        layout="vertical"
        autoComplete="off"
        onValuesChange={handleValuesChange}
        initialValues={{
          ...trip,
          pricing_type: trip.pricing_type || PRICING_TYPES.PER_PERSON,
          deposit_percentage: trip.deposit_percentage || 10,
          min_guests: trip.min_guests,
          max_guests: trip.max_guests
        }}
        style={{ maxWidth: "984px" }}
      >
        {/* Hidden fields to track min/max guests from Details tab */}
        <Form.Item name="min_guests" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="max_guests" hidden>
          <input type="hidden" />
        </Form.Item>

        <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
          <SeasonTypeSelector onChange={handleSeasonChange} value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR} />
          {isSeasonalExperience && (
            <>
              {timeBlocks.map((blockIndex) => {
                const dateRange = trip?.available_date_ranges?.[blockIndex]
                const allRanges = getAllSelectedDateRanges
                return (
                  <DateRangeBlock
                    key={blockIndex}
                    blockIndex={blockIndex}
                    onDelete={handleRemoveTimeBlock}
                    isSeasonalActive={isSeasonalExperience}
                    initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                    initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                    allRanges={allRanges}
                    timeBlocks={timeBlocks}
                  />
                )
              })}
              <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
                {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
              </Button>
              <Form.Item
                name="recurringYearly"
                valuePropName="checked"
                className="mt-l"
              >
                <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
              </Form.Item>
            </>
          )}
        </Card>
        <DaysAndHours form={form} />
        <GuestDetails licenses_required={licenses_required} form={form} licenses_list={licenses_list} />
        <LengthAndRates form={form} isShowErrorMessage={isShowErrorMessage} setIsShowErrorMessage={setIsShowErrorMessage} onPriceInteraction={handlePriceInteraction} />
      </CustomForm>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </Spin>
  )
}
